// report_builder/ReportRunner.jsx
// ============================================================
// REPORT RUNNER
// Run panel shown when user clicks Run on a saved report.
// Lets user pick:
//   - Scope: single symbol OR watchlist
//   - Date range (from / to) with quick presets
//   - Base timeframe (substitutes 'auto' in conditions)
// Then fires the report and passes results up to parent.
//
// Props:
//   report          — report object (id, name, base_timeframe)
//   onResults       — fn(results) called when run completes
//   onCancel        — fn() called when user cancels
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  runReport, fetchWatchlistsForReport,
  validateReport, fetchReportRuns,
} from './reportApi';

const TF_OPTIONS = ['2min', '5min', '15min', '1H', '1D', '1W'];

// ── Date helpers ─────────────────────────────────────────────
const fmt = (d) => d.toISOString().slice(0, 10);

const today = () => fmt(new Date());

const datePresets = [
  { label: '1M',   months: 1  },
  { label: '3M',   months: 3  },
  { label: '6M',   months: 6  },
  { label: '1Y',   years:  1  },
  { label: '2Y',   years:  2  },
  { label: '5Y',   years:  5  },
  { label: '10Y',  years:  10 },
  { label: '20Y',  years:  20 },
];

const calcFromDate = (preset) => {
  const d = new Date();
  if (preset.months) d.setMonth(d.getMonth() - preset.months);
  if (preset.years)  d.setFullYear(d.getFullYear() - preset.years);
  return fmt(d);
};


// ============================================================
// REPORT RUNNER
// ============================================================

export default function ReportRunner({ report, onResults, onCancel }) {
  // ── Scope ─────────────────────────────────────────────────
  const [scopeType,    setScopeType]    = useState('symbol');
  const [symbol,       setSymbol]       = useState('');
  const [watchlists,   setWatchlists]   = useState([]);
  const [watchlistId,  setWatchlistId]  = useState('');
  const [wlLoading,    setWlLoading]    = useState(false);

  // ── Date range ────────────────────────────────────────────
  const [fromDate,     setFromDate]     = useState(calcFromDate({ years: 1 }));
  const [toDate,       setToDate]       = useState(today());
  const [activePreset, setActivePreset] = useState('1Y');

  // ── Timeframe ─────────────────────────────────────────────
  const [timeframe,    setTimeframe]    = useState(
    report?.base_timeframe || '1D'
  );

  // ── Run state ─────────────────────────────────────────────
  const [running,      setRunning]      = useState(false);
  const [runError,     setRunError]     = useState(null);

  // ── R7: validate + past runs ──────────────────────────────
  const [validating,   setValidating]   = useState(false);
  const [valResult,    setValResult]    = useState(null);
  const [pastRuns,     setPastRuns]     = useState([]);
  useEffect(() => {
    if (!report?.id) return;
    fetchReportRuns(report.id)
      .then(data => setPastRuns(data || []))
      .catch(() => setPastRuns([]));
  }, [report]);

  // ── Load watchlists ───────────────────────────────────────
  useEffect(() => {
    setWlLoading(true);
    fetchWatchlistsForReport()
      .then(data => {
        setWatchlists(data || []);
        if (data?.length > 0) setWatchlistId(String(data[0].id));
        setWlLoading(false);
      })
      .catch(() => setWlLoading(false));
  }, []);


  // ── Preset click ──────────────────────────────────────────
  const handlePreset = (preset) => {
    setFromDate(calcFromDate(preset));
    setToDate(today());
    setActivePreset(preset.label);
  };

  // ── Run ───────────────────────────────────────────────────
  const handleRun = async () => {
    setRunError(null);

    // Validate
    if (scopeType === 'symbol' && !symbol.trim()) {
      setRunError('Enter a symbol to scan.');
      return;
    }
    if (scopeType === 'watchlist' && !watchlistId) {
      setRunError('Select a watchlist.');
      return;
    }
    if (!fromDate || !toDate) {
      setRunError('Select a date range.');
      return;
    }
    if (fromDate > toDate) {
      setRunError('From date must be before To date.');
      return;
    }

    setRunning(true);
    try {
      const results = await runReport({
        report_id:      report.id,
        scope_type:     scopeType,
        scope_value:    scopeType === 'symbol'
                          ? symbol.trim().toUpperCase()
                          : watchlistId,
        from_date:      fromDate,
        to_date:        toDate,
        base_timeframe: timeframe,
      });
      onResults(results);
    } catch (err) {
      setRunError(
        err?.response?.data?.detail || 'Report run failed. Check backend logs.'
      );
    } finally {
      setRunning(false);
    }
  };

  // ── R7: dry-run validation — same payload as Run, no scan ──
  const handleValidate = async () => {
    setRunError(null);
    setValResult(null);
    if (scopeType === 'symbol' && !symbol.trim()) {
      setRunError('Enter a symbol to validate.');
      return;
    }
    if (scopeType === 'watchlist' && !watchlistId) {
      setRunError('Select a watchlist.');
      return;
    }
    setValidating(true);
    try {
      const res = await validateReport({
        report_id:      report.id,
        scope_type:     scopeType,
        scope_value:    scopeType === 'symbol'
                          ? symbol.trim().toUpperCase()
                          : watchlistId,
        from_date:      fromDate,
        to_date:        toDate,
        base_timeframe: timeframe,
      });
      setValResult(res);
    } catch (err) {
      setRunError(
        err?.response?.data?.detail || 'Validation failed.'
      );
    } finally {
      setValidating(false);
    }
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={s.container}>

      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerTitle}>Run Report</span>
          <span style={s.headerSub}>{report?.name}</span>
        </div>
        <button style={s.btnSecondary} onClick={onCancel}>
          ← Back
        </button>
      </div>

      {/* ── Body ── */}
      <div style={s.body}>

        {/* ── Scope section ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Scope</div>

          {/* Scope type toggle */}
          <div style={s.toggleRow}>
            <button
              style={{ ...s.toggleBtn, ...(scopeType === 'symbol' ? s.toggleBtnActive : {}) }}
              onClick={() => setScopeType('symbol')}
            >
              Single Symbol
            </button>
            <button
              style={{ ...s.toggleBtn, ...(scopeType === 'watchlist' ? s.toggleBtnActive : {}) }}
              onClick={() => setScopeType('watchlist')}
            >
              Watchlist
            </button>
          </div>

          {/* Symbol input */}
          {scopeType === 'symbol' && (
            <div style={s.fieldRow}>
              <label style={s.label}>Symbol</label>
              <input
                style={s.textInput}
                value={symbol}
                onChange={e => setSymbol(e.target.value.toUpperCase())}
                placeholder="e.g. RELIANCE"
                onKeyDown={e => e.key === 'Enter' && handleRun()}
              />
            </div>
          )}

          {/* Watchlist picker */}
          {scopeType === 'watchlist' && (
            <div style={s.fieldRow}>
              <label style={s.label}>Watchlist</label>
              {wlLoading ? (
                <span style={s.loadingText}>Loading...</span>
              ) : watchlists.length === 0 ? (
                <span style={s.emptyText}>No watchlists found</span>
              ) : (
                <select
                  style={s.select}
                  value={watchlistId}
                  onChange={e => setWatchlistId(e.target.value)}
                >
                  {watchlists.map(wl => (
                    <option key={wl.id} value={String(wl.id)}>
                      {wl.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* ── Timeframe section ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Base Timeframe</div>
          <div style={s.tfRow}>
            {TF_OPTIONS.map(tf => (
              <button
                key={tf}
                style={{ ...s.tfBtn, ...(timeframe === tf ? s.tfBtnActive : {}) }}
                onClick={() => setTimeframe(tf)}
              >
                {tf}
              </button>
            ))}
          </div>
          <div style={s.tfHint}>
            Substitutes <strong>auto</strong> in all conditions.
            Chart opens in this timeframe.
          </div>
        </div>

        {/* ── Date range section ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Date Range</div>

          {/* Quick presets */}
          <div style={s.presetRow}>
            {datePresets.map(preset => (
              <button
                key={preset.label}
                style={{
                  ...s.presetBtn,
                  ...(activePreset === preset.label ? s.presetBtnActive : {}),
                }}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Manual date inputs */}
          <div style={s.dateRow}>
            <div style={s.fieldRow}>
              <label style={s.label}>From</label>
              <input
                type="date"
                style={s.dateInput}
                value={fromDate}
                onChange={e => {
                  setFromDate(e.target.value);
                  setActivePreset(null);
                }}
              />
            </div>
            <div style={s.fieldRow}>
              <label style={s.label}>To</label>
              <input
                type="date"
                style={s.dateInput}
                value={toDate}
                onChange={e => {
                  setToDate(e.target.value);
                  setActivePreset(null);
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Error message ── */}
        {runError && (
          <div style={s.errorBox}>
            {runError}
            <button style={s.msgClose} onClick={() => setRunError(null)}>✕</button>
          </div>
        )}

        {/* ── R7: validation result ── */}
        {valResult && (
          <div style={{
            padding: '10px 12px', borderRadius: '6px', fontSize: '12px',
            background: valResult.ok ? '#e8f5e9' : '#fff8e1',
            border: `1px solid ${valResult.ok ? '#a5d6a7' : '#ffe082'}`,
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {valResult.ok
                ? '✓ Validation passed — no issues found'
                : `⚠ ${valResult.issues.length} issue(s) found`}
              <button
                style={{ float: 'right', border: 'none',
                         background: 'none', cursor: 'pointer' }}
                onClick={() => setValResult(null)}
              >✕</button>
            </div>
            {(valResult.issues || []).map((x, i) => (
              <div key={`i${i}`} style={{ color: '#b26a00',
                                          padding: '1px 0' }}>{x}</div>
            ))}
            {(valResult.info || []).map((x, i) => (
              <div key={`n${i}`} style={{ color: '#2e7d32',
                                          padding: '1px 0' }}>{x}</div>
            ))}
          </div>
        )}

        {/* ── Validate + Run buttons ── */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={s.btnSecondary}
            onClick={handleValidate}
            disabled={validating || running}
          >
            {validating ? '⏳ Validating...' : '✓ Validate'}
          </button>
          <button
            style={{ ...s.btnRun, ...(running ? s.btnRunDisabled : {}) }}
            onClick={handleRun}
            disabled={running}
          >
            {running
              ? '⏳ Running...'
              : `▶  Run — ${report?.name}`}
          </button>
        </div>

        {/* ── Run info ── */}
        {running && (
          <div style={s.runningHint}>
            Scanning {scopeType === 'symbol'
              ? symbol
              : watchlists.find(w => String(w.id) === watchlistId)?.name || 'watchlist'
            } on {timeframe} from {fromDate} to {toDate}...
          </div>
        )}

        {/* ── R7: past runs ── */}
        {pastRuns.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>
              Past Runs ({pastRuns.length})
            </div>
            <div style={{ fontSize: '11px' }}>
              <div style={{ display: 'flex', gap: '8px',
                            fontWeight: 'bold', color: '#888',
                            padding: '2px 0',
                            borderBottom: '1px solid #eee' }}>
                <span style={{ width: '110px' }}>When</span>
                <span style={{ width: '120px' }}>Scope</span>
                <span style={{ width: '46px' }}>TF</span>
                <span style={{ width: '150px' }}>Range</span>
                <span style={{ width: '90px' }}>Matches</span>
                <span>Time</span>
              </div>
              {pastRuns.map(r => (
                <div key={r.id} style={{ display: 'flex', gap: '8px',
                                         padding: '3px 0', color: '#555',
                                         borderBottom: '1px solid #f5f5f5' }}>
                  <span style={{ width: '110px' }}>
                    {String(r.run_at).slice(0, 16).replace('T', ' ')}
                  </span>
                  <span style={{ width: '120px' }}>
                    {r.scope_type === 'symbol'
                      ? r.scope_value
                      : `WL ${r.scope_value}`}
                  </span>
                  <span style={{ width: '46px' }}>{r.base_timeframe}</span>
                  <span style={{ width: '150px' }}>
                    {r.from_date} → {r.to_date}
                  </span>
                  <span style={{ width: '90px', fontWeight: 'bold',
                                 color: r.total_matches > 0
                                   ? '#2e7d32' : '#aaa' }}>
                    {r.total_matches} ({r.symbols_with_matches} sym)
                  </span>
                  <span>{((r.duration_ms || 0) / 1000).toFixed(1)}s</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    overflow:      'hidden',
    background:    '#f7f8fa',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '10px 16px',
    background:     '#fff',
    borderBottom:   '1px solid #e0e0e0',
    flexShrink:     0,
  },
  headerLeft: {
    display:    'flex',
    alignItems: 'baseline',
    gap:        '10px',
  },
  headerTitle: {
    fontSize:   '14px',
    fontWeight: 'bold',
    color:      '#131722',
  },
  headerSub: {
    fontSize: '13px',
    color:    '#2962ff',
  },
  body: {
    flex:      1,
    overflowY: 'auto',
    padding:   '16px',
    display:   'flex',
    flexDirection: 'column',
    gap:       '12px',
    maxWidth:  '560px',
  },
  section: {
    background:   '#fff',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    padding:      '12px 14px',
  },
  sectionTitle: {
    fontSize:      '11px',
    fontWeight:    'bold',
    color:         '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom:  '10px',
  },
  toggleRow: {
    display:      'flex',
    gap:          '0px',
    marginBottom: '10px',
  },
  toggleBtn: {
    padding:         '5px 14px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  toggleBtnActive: {
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          '1px solid #2962ff',
  },
  fieldRow: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  label: {
    fontSize: '11px',
    color:    '#888',
  },
  textInput: {
    padding:      '6px 10px',
    fontSize:     '13px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    color:        '#333',
    width:        '200px',
    outline:      'none',
    textTransform:'uppercase',
  },
  select: {
    padding:      '6px 10px',
    fontSize:     '13px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    color:        '#333',
    background:   '#fff',
    cursor:       'pointer',
    width:        '220px',
  },
  tfRow: {
    display:      'flex',
    gap:          '4px',
    flexWrap:     'wrap',
    marginBottom: '6px',
  },
  tfBtn: {
    padding:         '4px 10px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  tfBtnActive: {
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          '1px solid #2962ff',
  },
  tfHint: {
    fontSize: '11px',
    color:    '#aaa',
    marginTop: '4px',
  },
  presetRow: {
    display:      'flex',
    gap:          '4px',
    marginBottom: '10px',
    flexWrap:     'wrap',
  },
  presetBtn: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  presetBtnActive: {
    backgroundColor: '#e8f0fe',
    color:           '#2962ff',
    border:          '1px solid #2962ff',
  },
  dateRow: {
    display: 'flex',
    gap:     '16px',
    flexWrap: 'wrap',
  },
  dateInput: {
    padding:      '6px 8px',
    fontSize:     '13px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    color:        '#333',
    cursor:       'pointer',
  },
  errorBox: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '8px 12px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    border:          '1px solid #ffcdd2',
    borderRadius:    '4px',
    fontSize:        '13px',
  },
  msgClose: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '14px',
    color:      'inherit',
  },
  btnRun: {
    padding:         '10px 20px',
    fontSize:        '14px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '6px',
    cursor:          'pointer',
    fontWeight:      'bold',
    alignSelf:       'flex-start',
  },
  btnRunDisabled: {
    backgroundColor: '#90a4d4',
    cursor:          'not-allowed',
  },
  btnSecondary: {
    padding:         '6px 14px',
    fontSize:        '13px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  runningHint: {
    fontSize: '12px',
    color:    '#888',
  },
  loadingText: {
    fontSize: '12px',
    color:    '#888',
  },
  emptyText: {
    fontSize: '12px',
    color:    '#aaa',
  },
};